import { sendAnalyticsEvent } from '@faststore/sdk'
import type { CurrencyCode, RemoveFromCartEvent } from '@faststore/sdk'
import type { AnalyticsItem } from 'src/sdk/analytics/types'
import { useCallback } from 'react'
import removeProductKpiFromOrderForm from 'src/utils/kpiToCart/removeProductKpiFromOrderForm'
import { getCookie } from 'src/utils/cookie'
import {
  getCategorie,
  getItemSizeFromProperties,
} from 'src/utils/getProductDetailsInfo'

import { useSession } from '../session'
import { useRemoveFromCart } from '../analytics/hooks/useRemoveFromCartEvent'
import { cartStore, useCart } from './index'
import type { CartItem } from '.'

export const useRemoveButton = (item: CartItem | null) => {
  const {
    person,
    currency: { code },
  } = useSession()

  const { sendRemoveFromCartEvent } = useRemoveFromCart()
  const { id } = useCart()
  const uuidToUse = getCookie('uuid')

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()

      if (!item) {
        return
      }

      const skuId = item?.itemOffered?.sku

      const filteredSku = item?.itemOffered?.isVariantOf?.hasVariant?.find(
        product => product.sku === skuId
      )

      const productIdMatch = item?.slug?.match(/\b[a-z]{2}\d{8}\b/)

      const productId = productIdMatch ? productIdMatch[0]?.toUpperCase() : ""

      const productName = item?.itemOffered?.name

      const productPrice = item?.price

      const productQuantity = item?.quantity

      const productDiscount = ((item?.listPrice - item?.price) * item?.quantity)

      const productType =
        item?.itemOffered?.name?.match(/^\p{L}+/u)?.[0] ?? ""

      const productWeight =
        item?.itemOffered?.name?.match(/(\d+)\s*k/i)?.[0] ?? ""

      const productBrand = item?.itemOffered?.brand?.name

      const productReference = filteredSku?.gtin ?? ""

      const categories = getCategorie(
        item?.breadcrumbList?.itemListElement?.map((category) => category),
        productName
      )

      const productCollection =
        String(
          item?.itemOffered?.isVariantOf?.additionalProperty?.find(
            (property) => property?.name?.toLowerCase() === 'coleção'
          )?.value ?? ''
        ) || ''

      const productMaterial =
        String(
          item?.itemOffered?.isVariantOf?.additionalProperty?.find(
            (property) => property?.name?.toLowerCase() === 'material'
          )?.value ?? ''
        ) || ''

      const productSize = getItemSizeFromProperties(
        item?.itemOffered?.isVariantOf?.additionalProperty,
        filteredSku?.additionalProperty
      )

      const productStone =
        String(
          item?.itemOffered?.isVariantOf?.additionalProperty?.find(
            (property) => property?.name?.toLowerCase() === 'pedras'.toLowerCase()
          )?.value ?? ''
        ) || ''

      const productAudience =
        String(
          item?.itemOffered?.isVariantOf?.additionalProperty?.find(
            (property) =>
              property?.name?.toLowerCase() === 'sugestão'.toLowerCase()
          )?.value ?? ''
        ) || ''

      sendRemoveFromCartEvent({
        sku_id: skuId,
        item_id: productId,
        item_name: productName,
        item_price: productPrice,
        item_type: productType,
        item_weight: productWeight,
        item_brand: productBrand,
        item_collection: productCollection,
        item_reference: productReference,
        item_material: productMaterial,
        item_category: categories[0] ?? "",
        item_category2: categories[1] ?? "",
        item_category3: categories[2] ?? "",
        item_category4: categories[3] ?? "",
        item_category5: categories[4] ?? "",
        item_category6: categories[5] ?? "",
        item_currency: 'BRL',
        item_size: productSize ?? "",
        item_stone: productStone,
        item_audience: productAudience,
        quantity: productQuantity,
        discount: productDiscount,
        currency: code as CurrencyCode,
        value: Number((item.price * item.quantity).toFixed(2)),
        user_logged: !!person?.id,
        user_id: person?.id ?? null,
        uuid: uuidToUse,
      })

      sendAnalyticsEvent<RemoveFromCartEvent<AnalyticsItem>>({
        name: 'remove_from_cart',
        params: {
          currency: code as CurrencyCode,
          value: item?.price * item.quantity,
          items: [
            {
              item_id: item.itemOffered?.sku,
              item_name: item.itemOffered?.isVariantOf?.name,
              item_brand: item.itemOffered?.brand?.name,
              item_variant: item.itemOffered?.sku,
              quantity: item.quantity,
              price: item?.price,
              discount: item?.listPrice - item?.price,
              currency: code as CurrencyCode,
              item_variant_name: item.itemOffered?.name,
              product_reference_id: productId,
              dimension1: item.itemOffered?.gtin,
            },
          ],
          // uuid: uuidToUse
        },
      })

      cartStore.removeItem(item.id)

      removeProductKpiFromOrderForm(id, item?.itemOffered?.sku)
    },
    [code, item]
  )

  return {
    onClick,
    'data-testid': 'remove-from-cart-button',
    'data-sku': item?.itemOffered?.sku,
  }
}